import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend } from
"recharts";
import api from "../../services/api";
import { PCOL } from "../../data/hrData";import "../../assets/styles/HR/AnalyticsPg.css";

function StatCard({ title, value, sub, color = "#4338ca" }) {
  return (
    <div className="cd">
      <div className="cd-b">
        <div className="analyticspg-inline-1">{title}</div>
        <div className="analyticspg-dyn-value" style={{ "--analyticspg-value-color": color }}>{value}</div>
        {sub ? <div className="analyticspg-inline-2">{sub}</div> : null}
      </div>
    </div>);

}

function AnalyticsPg() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    departmentPie: [],
    payrollTrend: [],
    headcountGrowth: [],
    attritionRate: [],
    attendanceByDepartment: [],
    leaveUtilization: [],
    onboardingFunnel: []
  });

  useEffect(() => {
    api.getHrAnalytics().
    then((res) => setData(res || {})).
    catch(() => setData({
      departmentPie: [],
      payrollTrend: [],
      headcountGrowth: [],
      attritionRate: [],
      attendanceByDepartment: [],
      leaveUtilization: [],
      onboardingFunnel: []
    })).
    finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    const totalEmployees = (data.departmentPie || []).reduce((s, x) => s + Number(x.value || 0), 0);
    const latestPayroll = (data.payrollTrend || []).length ? data.payrollTrend[data.payrollTrend.length - 1].totalLakhs : 0;
    const avgAttr = (data.attritionRate || []).length ?
    ((data.attritionRate || []).reduce((s, x) => s + Number(x.rate || 0), 0) / data.attritionRate.length).toFixed(2) :
    "0.00";
    const active = (data.onboardingFunnel || []).find((x) => x.stage === "Active")?.value || 0;
    return { totalEmployees, latestPayroll, avgAttr, active };
  }, [data]);

  if (loading) return <div className="analyticspg-inline-3">Loading analytics...</div>;

  return (
    <div>
      <div className="sec">HR Analytics</div>

      <div className="g4 mb16 analyticspg-inline-4">
        <StatCard title="Total Employees" value={totals.totalEmployees} sub="Employee + Intern active pool" color="#4338ca" />
        <StatCard title="Latest Payroll" value={`${totals.latestPayroll} L`} sub="Net payroll in lakhs" color="#2563eb" />
        <StatCard title="Avg Attrition" value={`${totals.avgAttr}%`} sub="Month-wise avg exit rate" color="#ef4444" />
        <StatCard title="Onboarding Active" value={totals.active} sub="Funnel final stage count" color="#2563eb" />
      </div>

      <div className="g2 mb16">
        <div className="cd">
          <div className="cd-h"><div className="cd-t">Total Employees by Department (Pie)</div></div>
          <div className="cd-b analyticspg-inline-5">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.departmentPie || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={86} label={({ name, value }) => `${name} ${value}`}>
                  {(data.departmentPie || []).map((_, i) => <Cell key={i} fill={PCOL[i % PCOL.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cd">
          <div className="cd-h"><div className="cd-t">Leave Utilization by Leave Type (Donut)</div></div>
          <div className="cd-b analyticspg-inline-6">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.leaveUtilization || []} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={86} label>
                  {(data.leaveUtilization || []).map((_, i) => <Cell key={i} fill={PCOL[(i + 3) % PCOL.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="g2 mb16">
        <div className="cd">
          <div className="cd-h"><div className="cd-t">Monthly Payroll Trend (Line/Area)</div></div>
          <div className="cd-b analyticspg-inline-7">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.payrollTrend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="totalLakhs" stroke="#2563eb" fill="#dbeafe" name="Payroll (L)" />
                <Line type="monotone" dataKey="totalLakhs" stroke="#15803d" strokeWidth={2} dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cd">
          <div className="cd-h"><div className="cd-t">Headcount Growth Month-wise (Bar)</div></div>
          <div className="cd-b analyticspg-inline-8">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.headcountGrowth || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="joins" fill="#4338ca" radius={[4, 4, 0, 0]} name="Joins" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="g2 mb16">
        <div className="cd">
          <div className="cd-h"><div className="cd-t">Attrition Rate Month-wise (Line)</div></div>
          <div className="cd-b analyticspg-inline-9">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.attritionRate || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" />
                <YAxis unit="%" />
                <Tooltip />
                <Line type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4 }} name="Attrition %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cd">
          <div className="cd-h"><div className="cd-t">Attendance % by Department (Stacked Bar)</div></div>
          <div className="cd-b analyticspg-inline-10">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.attendanceByDepartment || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="department" />
                <YAxis unit="%" />
                <Tooltip />
                <Legend />
                <Bar dataKey="presentPct" stackId="a" fill="#2563eb" name="Present %" />
                <Bar dataKey="absentPct" stackId="a" fill="#ef4444" name="Absent %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="cd">
        <div className="cd-h"><div className="cd-t">Onboarding Funnel (Applied → Submitted → Verified → Active)</div></div>
        <div className="cd-b">
          <div className="analyticspg-inline-11">
            {(data.onboardingFunnel || []).map((f, i) =>
            <div key={f.stage} style={{ "--analyticspg-1-background": i % 2 ? "#f8fafc" : "#ffffff" }} className="analyticspg-dyn-1">
                <div className="analyticspg-inline-12">{f.stage}</div>
                <div className="analyticspg-inline-13">{f.value || 0}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>);

}

export default AnalyticsPg;
